/* Soulskart policy pages — layout enhancement.

   Shopify has no `policy` template type, so the body of /policies/* is markup
   the theme cannot replace. See the header of ss-policy.css for the evidence.
   This script takes what Shopify renders:

     main.content-for-layout
       div.shopify-policy__container
         div.shopify-policy__title > h1
         div.shopify-policy__body  > div.rte

   and rebuilds it as a hero band, a two column body with a contents rail and a
   policy switcher, and a help band.

   IT MOVES NODES, IT DOES NOT CLONE THEM. appendChild on an attached element
   relocates it, so the h1 and the .rte exist exactly once. If this file fails to
   load the page is still fully styled and readable — ss-policy.css targets the
   Shopify class names as well as ours.

   EVERYTHING GOES INSIDE A .sk WRAPPER, AND THAT IS NOT COSMETIC.
   The SK design tokens are declared on the .sk class, not on :root. Measured:
   --sk-max, --sk-gut, --sk-display, --sk-ink and --sk-on-ink all resolve to the
   empty string on document.documentElement and only take values inside .sk. The
   first build inserted these wrappers straight into <main>, so .sk-in evaluated
   max-width:var(--sk-max) and padding-inline:var(--sk-gut) against nothing and
   collapsed to max-width:none with zero padding — the whole page ran edge to
   edge at 1800px wide. .sk carries only custom properties, no layout, so
   wrapping is free.

   Config comes from window.SS_POLICY, emitted by snippets/ss-tokens.liquid, so
   policy titles and URLs come from the shop object rather than being typed here.

   The contents list is built from h2/h3 at runtime and the whole block hides
   when there are none — two of the five policies on this store are an unbroken
   run of paragraphs with no headings at all. */
(() => {
  if (window.__ssPolicy) return;
  const container = document.querySelector('.shopify-policy__container');
  if (!container) return;
  const h1 = container.querySelector('h1');
  const rte = container.querySelector('.rte');
  if (!h1 || !rte) return;
  window.__ssPolicy = true;

  const cfg = window.SS_POLICY || {};
  const main = container.parentElement;

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  /* Token scope. See the note above — without this the layout has no gutters. */
  const root = el('div', 'sk skp');

  /* ---------------- hero ------------------------------------------------ */
  const hero = el('div', 'skp__hero');
  const heroIn = el('div', 'sk-in');
  if (cfg.eyebrow) heroIn.appendChild(el('p', 'sk-mono skp__eye', cfg.eyebrow));
  h1.className = 'skp__h';
  heroIn.appendChild(h1);
  if (cfg.summary) heroIn.appendChild(el('p', 'skp__lede', cfg.summary));

  const facts = (cfg.facts || []).filter((f) => f && f[0]);
  if (facts.length) {
    const ul = el('ul', 'skp__facts');
    ul.setAttribute('role', 'list');
    facts.forEach((f) => {
      const li = el('li', 'skp__fact');
      li.appendChild(el('b', null, f[0]));
      li.appendChild(el('span', 'sk-mono', f[1] || ''));
      ul.appendChild(li);
    });
    heroIn.appendChild(ul);
  }
  hero.appendChild(heroIn);

  /* ---------------- body + rail ----------------------------------------- */
  const wrap = el('div', 'skp__main sk-in');
  const rail = el('aside', 'skp__rail');

  const tocBox = el('div', 'skp__toc');
  tocBox.appendChild(el('p', 'sk-mono skp__rail-t', 'On this page'));
  const tocNav = el('nav');
  tocNav.setAttribute('aria-label', 'On this page');
  const tocList = el('ul');
  tocNav.appendChild(tocList);
  tocBox.appendChild(tocNav);
  rail.appendChild(tocBox);

  const links = cfg.links || [];
  if (links.length) {
    const sw = el('div', 'skp__switch');
    sw.appendChild(el('p', 'sk-mono skp__rail-t', 'All policies'));
    const nav = el('nav');
    nav.setAttribute('aria-label', 'All policies');
    const ul = el('ul');
    ul.setAttribute('role', 'list');
    links.forEach((l) => {
      if (!l || !l.url) return;
      const li = el('li');
      const a = el('a', null, l.title || l.url);
      a.href = l.url;
      if (l.current) a.setAttribute('aria-current', 'page');
      li.appendChild(a);
      ul.appendChild(li);
    });
    nav.appendChild(ul);
    sw.appendChild(nav);
    rail.appendChild(sw);
  }

  const doc = el('article', 'skp__doc');
  doc.appendChild(rte);
  wrap.appendChild(rail);
  wrap.appendChild(doc);

  /* ---------------- contents, built from the text itself ----------------- */
  const slugify = (s) =>
    s.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 60);

  const items = [];
  rte.querySelectorAll('h2, h3').forEach((h, i) => {
    const text = h.textContent.trim();
    if (!text) return;
    if (!h.id) {
      const base = slugify(text) || 'section-' + i;
      let slug = base;
      let n = 1;
      while (document.getElementById(slug)) slug = base + '-' + n++;
      h.id = slug;
    }
    const li = el('li', 'skp__toc-item' + (h.tagName === 'H3' ? ' skp__toc-item--sub' : ''));
    const a = el('a', 'skp__toc-link', text);
    a.href = '#' + h.id;
    li.appendChild(a);
    tocList.appendChild(li);
    items.push({ h: h, a: a });
  });
  if (!items.length) tocBox.hidden = true;

  /* ---------------- help band ------------------------------------------- */
  const H = cfg.help || {};
  const chips = [];
  if (H.faq) chips.push({ t: 'Read the FAQs', u: H.faq, ext: false });
  if (H.contact) chips.push({ t: 'Contact us', u: H.contact, ext: false });
  if (H.whatsapp) chips.push({ t: 'WhatsApp', u: 'https://wa.me/' + H.whatsapp, ext: true });

  let help = null;
  if (chips.length) {
    help = el('div', 'skp__help sk-in');
    const box = el('div', 'skp__help-in');
    const left = el('div');
    if (H.eyebrow) left.appendChild(el('p', 'sk-mono skp__rail-t', H.eyebrow));
    left.appendChild(el('p', 'skp__help-h', H.heading || 'A human can sort it out faster.'));
    const row = el('div', 'skp__help-links');
    chips.forEach((c, i) => {
      const a = el('a', 'skp__chip' + (i === 0 ? ' skp__chip--acc' : ''), c.t);
      a.href = c.u;
      if (c.ext) { a.rel = 'noopener'; a.target = '_blank'; }
      row.appendChild(a);
    });
    box.appendChild(left);
    box.appendChild(row);
    help.appendChild(box);
  }

  /* ---------------- swap in --------------------------------------------- */
  root.appendChild(hero);
  root.appendChild(wrap);
  if (help) root.appendChild(help);
  main.insertBefore(root, container);
  container.remove();

  /* Highlight the heading currently in view. */
  if (items.length && 'IntersectionObserver' in window) {
    const byId = {};
    items.forEach((it) => { byId[it.h.id] = it.a; });
    let current = null;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const a = byId[e.target.id];
          if (a && a !== current) {
            if (current) current.classList.remove('is-active');
            a.classList.add('is-active');
            current = a;
          }
        });
      },
      { rootMargin: '0px 0px -72% 0px', threshold: 0 }
    );
    items.forEach((it) => io.observe(it.h));
  }
})();
